import { faPaperclip } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@progress/kendo-react-buttons";
import {
  Calendar,
  CalendarCell,
  DatePicker,
  DateTimePicker,
  TimePicker,
} from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, NumericTextBox, Signature, TextArea } from "@progress/kendo-react-inputs";
import { Suspense } from "react";
import { isValidDate } from "../../Utilities/DateUtils";
import { setStyleViewEntry } from "../../Utilities/EntryUtils";
import {
  findDatasetId,
  formatNumericValue,
  formatTimeString,
  getRevisionCount,
  reAddTimeOffset,
  updateDatabase,
  updateOriginalItem,
  userCanModifyEntry,
} from "../../Utilities/ViewEntryUtils";
import { commentsData, commentsFieldName, showCommentsWindow } from "./CommentsViewWindow";
import { imageLabelFieldName, imageLabelSrc, showImageLabelWindow } from "./ImageLabelViewWindow";

// ─── Calendar Cells ───────────────────────────────────────────────────────────

// Renders a calendar cell with weekend days disabled and styled in red
export const customCell = (event) => {
  const isWeekend = event.isWeekend;
  const style = event.isWeekend ? { color: "red" } : { fontWeight: "normal" };

  return (
    <CalendarCell
      {...event}
      style={style}
      isDisabled={isWeekend}
    />
  );
};

// Custom calendar component that uses customCell for weekday-only selection
export const CustomCalendar = (event) => {
  return (
    <Calendar
      {...event}
      cell={customCell}
      className="customCalendar"
    />
  );
};

// ─── Detail View Read-Only Cells ──────────────────────────────────────────────

// Renders a read-only datetime cell formatted as short date/time
export const DateTimeCell = (event, field) => {
  return (
    <td
      title={event.dataItem[field.fieldName]}
      data-testid={field.fieldName + "_" + event.dataIndex}
    >
      {isValidDate(event.dataItem[field.fieldName])
        ? new Intl.DateTimeFormat("en-US", {
            dateStyle: "short",
            timeStyle: "short",
          }).format(new Date(event.dataItem[field.fieldName]))
        : ""}
    </td>
  );
};

// Renders a read-only date cell formatted in UTC timezone
export const DateCell = (event, field) => {
  return (
    <td
      title={event.dataItem[field.fieldName]}
      data-testid={field.fieldName + "_" + event.dataIndex}
    >
      {isValidDate(event.dataItem[field.fieldName])
        ? new Intl.DateTimeFormat("en-US", { timeZone: "UTC" }).format(
            new Date(event.dataItem[field.fieldName])
          )
        : ""}
    </td>
  );
};

// Renders a read-only time cell formatted as 12-hour time
export const TimeCell = (event, field) => {
  return (
    <td
      title={event.dataItem[field.fieldName]}
      data-testid={field.fieldName + "_" + event.dataIndex}
    >
      {isValidDate(event.dataItem[field.fieldName])
        ? formatTimeString(event.dataItem[field.fieldName])
        : ""}
    </td>
  );
};

// Renders a read-only cell displaying person/group display names
export const PeoplePickerCell = (event, field) => {
  const personObject = JSON.tryParse(event.dataItem[field.fieldName]);

  if (!Array.isArray(personObject) || personObject.length === 0) {
    return <td></td>;
  }

  return (
    <td>
      <Suspense fallback={<div>Loading...</div>}>
        {personObject.map((person) => (
          <div
            key={person.id}
            title={person.displayName}
          >
            {person.displayName}
          </div>
        ))}
      </Suspense>
    </td>
  );
};

// Renders a default read-only text cell
export const DefaultCell = (event, field) => {
  return (
    <td
      title={event.dataItem[field.fieldName]}
      data-testid={field.fieldName + "_" + event.dataIndex}
    >
      <div className={"k-rounded-lg k-pl-2 k-pr-2 k-d-inline-block"}>
        {event.dataItem[field.fieldName]}
      </div>
    </td>
  );
};

// ─── Grid Editable/Read-Only Cell Renderers ───────────────────────────────────

// Renders a dropdown cell for fields with dataset options (editable if permitted)
export function renderGridColumnCell(params) {
  const {
    event,
    field,
    allDatasets,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    const datasetId = findDatasetId(event.dataItem.originalItem.sections, event.field);
    const dataset = allDatasets?.find((dataset) => dataset.id === datasetId);
    let options = dataset?.values;

    if (field.fieldProperties?.["ParentFieldName"]) {
      const parentValue = event.dataItem[field.fieldProperties["ParentFieldName"]];
      options = options?.filter((option) => option.filterValue === parentValue);
    }

    options = options?.map((option) => option.value);

    return (
      <td
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "span.k-combobox",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <DropDownList
          name={field.fieldName}
          data={options}
          value={event.dataItem[field.fieldName]}
          onChange={(e) => {
            updateOriginalItem(
              event,
              field.fieldName,
              e.target.value,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(event.dataItem.originalItem, e.target, userEmail, selectedTemplate);
          }}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "div",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
      >
        <div className={"k-rounded-lg k-pl-2 k-pr-2 k-d-inline-block"}>
          {event.dataItem[field.fieldName]}
        </div>
      </td>
    );
  }
}

// Renders a text input cell (editable if permitted)
export function renderTextFieldCell(params) {
  const {
    event,
    field,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    return (
      <td
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "input",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <Input
          name={field.fieldName}
          defaultValue={event.dataItem[field.fieldName]}
          onBlur={(e) => {
            updateOriginalItem(
              event,
              field.fieldName,
              e.target.value,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(event.dataItem.originalItem, e.target, userEmail, selectedTemplate);
          }}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "div",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
      >
        <div className={"k-rounded-lg k-pl-2 k-pr-2 k-d-inline-block"}>
          {event.dataItem[field.fieldName]}
        </div>
      </td>
    );
  }
}

// Renders a multi-line text area cell (editable if permitted)
export function renderTextAreaCell(params) {
  const {
    event,
    field,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    return (
      <td
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "input",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <TextArea
          name={field.fieldName}
          defaultValue={event.dataItem[field.fieldName]}
          autoSize={true}
          onBlur={(e) => {
            updateOriginalItem(
              event,
              field.fieldName,
              e.nativeEvent.originalTarget.value,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(
              event.dataItem.originalItem,
              e.nativeEvent.originalTarget,
              userEmail,
              selectedTemplate
            );
          }}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "div",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
      >
        <div className={"k-rounded-lg k-pl-2 k-pr-2 k-d-inline-block"}>
          {event.dataItem[field.fieldName]}
        </div>
      </td>
    );
  }
}

// Renders a numeric input cell (editable if permitted)
export function renderNumericCell(params) {
  const {
    event,
    field,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    return (
      <td
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "input",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <NumericTextBox
          name={field.fieldName}
          defaultValue={
            event.dataItem[field.fieldName] !== null &&
            event.dataItem[field.fieldName] !== undefined &&
            event.dataItem[field.fieldName] !== ""
              ? Number(event.dataItem[field.fieldName])
              : null
          }
          onBlur={(e) => {
            updateOriginalItem(
              event,
              field.fieldName,
              e.target.value,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(event.dataItem.originalItem, e.target, userEmail, selectedTemplate);
          }}
          format={field.fieldProperties?.["NumericFormat"] ?? "n"}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
        ref={(el) => {
          if (el && field.fieldProperties?.["ConditionalFormatting"]) {
            setStyleViewEntry(
              el,
              "div",
              field.fieldProperties["ConditionalFormatting"],
              event.dataItem
            );
          }
        }}
      >
        <div className={"k-rounded-lg k-pl-2 k-pr-2 k-d-inline-block"}>
          {formatNumericValue(
            event.dataItem[field.fieldName],
            field.fieldProperties?.["NumericFormat"]
          )}
        </div>
      </td>
    );
  }
}

// Renders a date picker cell with revision tracking (editable if permitted)
export function renderDateCell(params) {
  const {
    event,
    field,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    updateRevisionCount,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <DatePicker
          calendar={field?.fieldProperties?.["WeekDaysOnly"] ? CustomCalendar : null}
          name={field.fieldName + "_" + event.dataIndex}
          value={
            isValidDate(event.dataItem[field.fieldName])
              ? new Date(
                  new Intl.DateTimeFormat("en-US", {
                    timeZone: "UTC",
                    dateStyle: "short",
                  }).format(new Date(event.dataItem[field.fieldName]))
                )
              : ""
          }
          onChange={(e) => {
            const updatedValue = e.value ? new Date(e.value) : "";
            const timezoneOffset = new Date().getTimezoneOffset() * 60000;
            const convertedDate =
              new Date(updatedValue.getTime() - timezoneOffset).toISOString().slice(0, 10) +
              "T00:00:00.000";
            event.dataItem.originalItem = updateRevisionCount(
              event.dataItem.originalItem,
              e.target,
              event.dataItem,
              convertedDate
            );
            if (!isNaN(updatedValue) && updatedValue instanceof Date) {
              event.dataItem[field.fieldName] = convertedDate;
            } else {
              event.dataItem[field.fieldName] = null;
              e.value = null;
            }
            updateOriginalItem(
              event,
              field.fieldName,
              convertedDate,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(event.dataItem.originalItem, e.target, userEmail, selectedTemplate);
          }}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        {isValidDate(event.dataItem[field.fieldName])
          ? new Intl.DateTimeFormat("en-US", { timeZone: "UTC" }).format(
              new Date(event.dataItem[field.fieldName])
            )
          : ""}
      </td>
    );
  }
}

// Renders a datetime picker cell with revision tracking (editable if permitted)
export function renderDateTimeCell(params) {
  const {
    event,
    field,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    updateRevisionCount,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    let dateValue = new Date(event.dataItem[field.fieldName]);
    if (event.dataItem[field.fieldName]?.toString()?.includes("Z")) {
      dateValue = reAddTimeOffset(event.dataItem[field.fieldName]);
    }

    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <DateTimePicker
          calendar={field?.fieldProperties?.["WeekDaysOnly"] ? CustomCalendar : null}
          name={field.fieldName}
          value={isValidDate(event.dataItem[field.fieldName]) ? dateValue : ""}
          onChange={(e) => {
            const newDate = new Date(e.target.value);
            const timezoneOffset = new Date().getTimezoneOffset() * 60000;
            const convertedDate = new Date(newDate.getTime() - timezoneOffset)
              .toISOString()
              .replace("Z", "");
            event.dataItem.originalItem = updateRevisionCount(
              event.dataItem.originalItem,
              e.target,
              event.dataItem,
              convertedDate
            );
            event.dataItem[field.fieldName] = convertedDate;
            updateOriginalItem(
              event,
              field.fieldName,
              convertedDate,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(event.dataItem.originalItem, e.target, userEmail, selectedTemplate);
          }}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        {isValidDate(event.dataItem[field.fieldName])
          ? new Intl.DateTimeFormat("en-US", {
              dateStyle: "short",
              timeStyle: "short",
            }).format(new Date(event.dataItem[field.fieldName]))
          : ""}
      </td>
    );
  }
}

// Renders a time picker cell (editable if permitted)
export function renderTimeCell(params) {
  const {
    event,
    field,
    userIsAdmin,
    selectedTemplate,
    userEmail,
    filteredGridData,
    setFilteredGridData,
  } = params;
  if (
    field.fieldProperties?.["IsEditable"] &&
    userCanModifyEntry(
      userIsAdmin,
      selectedTemplate,
      userEmail,
      event.dataItem.originalItem.entryAuthor
    )
  ) {
    let timeString = event.dataItem[field.fieldName];

    if (timeString?.toString()?.includes("Z")) {
      timeString = reAddTimeOffset(timeString);
    }

    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        <TimePicker
          name={field.fieldName}
          value={isNaN(new Date(timeString)) ? null : new Date(timeString)}
          onChange={(e) => {
            const newDate = new Date(e.target.value);
            const timezoneOffset = new Date().getTimezoneOffset() * 60000;
            const convertedDate = new Date(newDate.getTime() - timezoneOffset)
              .toISOString()
              .replace("Z", "");
            event.dataItem[field.fieldName] = convertedDate;
            updateOriginalItem(
              event,
              field.fieldName,
              convertedDate,
              filteredGridData,
              setFilteredGridData
            );
            updateDatabase(event.dataItem.originalItem, e.target, userEmail, selectedTemplate);
          }}
        />
      </td>
    );
  } else {
    return (
      <td
        title={event.dataItem[field.fieldName]}
        data-testid={field.fieldName + "_" + event.dataIndex}
      >
        {isValidDate(event.dataItem[field.fieldName])
          ? formatTimeString(event.dataItem[field.fieldName])
          : ""}
      </td>
    );
  }
}

// ─── Special Cell Renderers ──────────────────────────────────────────────────

// Renders the revision count for date fields
export function renderDateRevisionsCell(event, field) {
  const revisionCount = getRevisionCount(event.dataItem.originalItem, field.fieldName);
  return (
    <td
      title={event.dataItem[field.fieldName + " Revisions"]}
      data-testid={field.fieldName + "_" + revisionCount + "_" + event.dataIndex}
    >
      {revisionCount}
    </td>
  );
}

// Renders the action ID associated with a business unit field
export function renderActionIdsCell(e) {
  return (
    <td
      title={e.event.dataItem[e.field.fieldName + " Action Ids"]}
      data-testid={e.field.fieldName + "_" + e.event.dataIndex}
    >
      {e.event.dataItem.originalItem.actionId}
    </td>
  );
}

// Renders the entry author email
export function renderAuthorCell(event) {
  return (
    <td title={event.dataItem.originalItem.entryAuthor}>
      {event.dataItem.originalItem.entryAuthor}
    </td>
  );
}

// Renders an attachment indicator icon if files are present
export function renderAttachmentCell({ event, field }) {
  if (event.dataItem[field.fieldName] === "" || event.dataItem[field.fieldName] === "[]")
    return <td></td>;
  return (
    <td>
      <FontAwesomeIcon icon={faPaperclip} />
    </td>
  );
}

// Renders a clickable hyperlink from JSON field data
export function renderHyperlinkCell({ event, field }) {
  if (
    event?.dataItem[field.fieldName] === "" ||
    event?.dataItem[field.fieldName] === "{}" ||
    !event?.dataItem[field.fieldName]
  )
    return <td></td>;
  return (
    <td>
      <a
        href={JSON.tryParse(event.dataItem[field.fieldName])?.href}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: "underline", color: "blue" }}
      >
        {JSON.tryParse(event.dataItem[field.fieldName])?.text}
      </a>
    </td>
  );
}

// ─── Comments / Signature Grid Cells ─────────────────────────────────────────

// Renders a button to view comments with count indicator
export function renderCommentsCell(event, field) {
  try {
    const commentsArr = JSON.tryParse(event.dataItem[field.fieldName], []);
    return (
      <td>
        <Button
          onClick={() => {
            commentsData.value = commentsArr;
            commentsFieldName.value = field.fieldName;
            showCommentsWindow.value = true;
          }}
          disabled={!Array.isArray(commentsArr) || commentsArr.length === 0}
        >
          View Comments ({commentsArr.length})
        </Button>
      </td>
    );
  } catch (e) {
    console.error(e);
    return <td></td>;
  }
}

// Renders a button to view the image configured on an ImageLabel field
export function renderImageLabelCell(event, field) {
  const src = field.fieldProperties?.ImageSrc || "";
  return (
    <td>
      <Button
        onClick={() => {
          imageLabelSrc.value = src;
          imageLabelFieldName.value = field.fieldName;
          showImageLabelWindow.value = true;
        }}
        disabled={!src}
      >
        View Image
      </Button>
    </td>
  );
}

// Renders a read-only signature component
export function renderSignatureCell(event, field) {
  try {
    return (
      <td>
        <Signature
          value={event.dataItem[field.fieldName]}
          smooth={true}
          maximizable={true}
          hideLine={true}
          valid={true}
          readOnly={true}
        />
      </td>
    );
  } catch (e) {
    console.error(e);
    return <td></td>;
  }
}
